import React, { ReactNode, useEffect, useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { useParams } from 'react-router'
import { NavLink } from 'react-router-dom'
import styled from 'styled-components'
import sortBy from 'lodash/sortBy'
import { RouteProps } from 'types'
import { spacesStatusSelector } from 'features/events/eventSlice'
import { _, colors } from 'utils'
import { addModal } from 'features/modals/modalSlice'
import CreateSpace from 'features/spaces/CreateSpace'
import { getSpaceIconName } from 'features/spaces/utils'
import { useEventSlug } from 'hooks'
import Button from '../../Button'
import { SidebarItem } from './getSidebarConfig'
import Icon from '../../Icon'

interface RouteLinkProps {
    className?: string
    route?: RouteProps
    path?: string
    children: ReactNode
    searchParams?: string
}

const RouteLink = ({
    className, route, path, children, searchParams=''
}: RouteLinkProps) => {
    const params = useParams()
    const to = path || `${route?.getPath?.(params)}${searchParams}`
    return (route || path) ? (
        <NavLink to={to} className={className}>
            {children}
        </NavLink>
    ) : (
        <span className={className}>{children}</span>
    )
}

interface StyledProps {
    isShowSubmenu: boolean
}

const S = {
    SidebarLink: styled(RouteLink)`
        display: flex;
        justify-content: space-between;
        align-items: center;

        height: 45px;
        margin-bottom: 10px;
        padding-left: 32px;
        padding-right: 18px;

        text-decoration: none;
        font-size: 14px;
        line-height: 16px;
        color: ${colors.sidebarItem};
        text-transform: uppercase;
        cursor: pointer;

        &:hover, &.active {
            color: white;
            text-decoration: none;
            & svg > path {
                fill: white !important;
            }
        }
    `,
    NavItemWrapper: styled.div`
        display: flex;
        align-items: center;
        overflow: hidden;
        white-space: nowrap;
        text-overflow: ellipsis;
    `,
    DropDownIcon: styled(Icon)<StyledProps>`
        margin-bottom: 4px;
        transition: 0.4s;
        transform: rotate(${({ isShowSubmenu }) => (isShowSubmenu ? -180 : 0)}deg);
    `,
    Icon: styled(Icon)`
        margin-bottom: 3px;
        margin-right: 16px;
    `,
    DropdownLink: styled(RouteLink)<StyledProps>`
        display: flex;
        align-items: center;
        height: ${({ isShowSubmenu }) => (isShowSubmenu ? 45 : 0)}px;
        padding-left: 64px;
        opacity: ${({ isShowSubmenu }) => (isShowSubmenu ? 1 : 0)};
        text-decoration: none;
        color: ${colors.sidebarItem};
        font-size: 14px;
        transition: 0.3s;
        overflow: hidden;
    
        &:hover, &.active {
            color: white;
            text-decoration: none;
            & > span svg path {
                fill: white !important;
            }
        }
    `,
    DropdownWrapper: styled.div<StyledProps>`
        width: 100%;
        & > a:last-child {
            margin-bottom: ${({ isShowSubmenu }) => (isShowSubmenu ? 10 : 0)}px;
        }
    `,
    SpacesStatus: styled.div`
        width: 30px;
        height: 18px;
        margin-right: 2px;
        border-radius: 2px;
        background: ${colors.primary};
        color: white;
        font-size: 10px;
        line-height: 18px;
        text-align: center;
    `,
    CreateSpaceButton: styled(Button)`
        width: 140px;
        margin-left: 32px;
        margin-bottom: 32px;
        background-color: ${colors.keppel};
    `
}

const SubMenu = ({
    item, currentOpen, onChangeCurrentOpen
}: { item: SidebarItem, currentOpen: string, onChangeCurrentOpen: (s) => void }) => {
    const dispatch = useDispatch()
    const eventSlug = useEventSlug()
    const [isShowSubmenu, setIsShowSubmenu] = useState(false)
    const {
        numberOfSpaces, maxSpaces, boothSpaces, maxBooths, normalSpaces
    } = useSelector(spacesStatusSelector)
    const showSubmenu = () => {
        setIsShowSubmenu(!isShowSubmenu)
        onChangeCurrentOpen(item.title)
    }
    const showCreateSpaceModal = () => {
        dispatch(addModal({
            Component: ({ close }: { close: () => void }) => (
                <CreateSpace close={close} />
            ),
            shouldNotWrap: true
        }))
    }
    useEffect(() => {
        if (currentOpen !== item.title) {
            setIsShowSubmenu(false)
        }
    }, [currentOpen, item.title])

    return (
        <div>
            <div onClick={showSubmenu}>
                <S.SidebarLink route={item.route} searchParams={item.searchParams}>
                    <S.NavItemWrapper>
                        <S.Icon icon={item.icon} color={colors.sidebarItem} size={16} />
                        <span>{_(item.title)}</span>
                    </S.NavItemWrapper>
                    <div className="flex-space-between">
                        {item.spaces === 'booth' && (
                            <S.SpacesStatus>
                                {`${boothSpaces}/${maxBooths}`}
                            </S.SpacesStatus>
                        )}
                        {item.spaces === 'normal' && (
                            <S.SpacesStatus>
                                {`${numberOfSpaces}/${maxSpaces}`}
                            </S.SpacesStatus>
                        )}
                        {item.subMenu
                            ? (
                                <S.DropDownIcon
                                    icon="chevronDown"
                                    isShowSubmenu={isShowSubmenu}
                                    color={colors.fadeIcon}
                                    size={18}
                                />
                            )
                            : (
                                <div style={{ width: 18 }} />
                            )}
                    </div>
                </S.SidebarLink>
            </div>
            <S.DropdownWrapper isShowSubmenu={isShowSubmenu}>
                {
                    item?.subMenu?.map((subItem, index) => (
                        <S.DropdownLink
                            searchParams={subItem.searchParams}
                            route={subItem.route}
                            key={index}
                            isShowSubmenu={isShowSubmenu}
                        >
                            <S.NavItemWrapper>
                                {subItem.icon && (
                                    <S.Icon icon={subItem.icon} color={colors.sidebarItem} size={16} />
                                )}
                                <span>{_(subItem.title)}</span>
                            </S.NavItemWrapper>
                        </S.DropdownLink>
                    ))
                }
                {
                    item.spaces === 'normal' && (
                        <>
                            {
                                sortBy(normalSpaces, 'timetableOrder').map((space, index) => (
                                    <S.DropdownLink
                                        key={index}
                                        path={`/${eventSlug}/${space.type}/${space.slug}`}
                                        isShowSubmenu={isShowSubmenu}
                                    >
                                        <S.Icon
                                            icon={getSpaceIconName(space.type)}
                                            color={colors.sidebarItem}
                                            size={16}
                                            style={{ marginRight: 6 }}
                                        />
                                        <span>{space.name}</span>
                                    </S.DropdownLink>
                                ))
                            }
                            {
                                isShowSubmenu && (
                                    <S.CreateSpaceButton
                                        icon="plusBox"
                                        onClick={showCreateSpaceModal}
                                        disabled={numberOfSpaces >= maxSpaces}
                                    >
                                        &nbsp;
                                        {_('space.create_space')}
                                    </S.CreateSpaceButton>
                                )
                            }
                        </>
                    )
                }
            </S.DropdownWrapper>
        </div>
    )
}

export default SubMenu
